import React, { useState, useEffect } from 'react';
import '../styles/dialog.css';
import { Typography, Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../utils/api';
import { BridgeTypes } from '@flashbacktech/flashbackclient/api';

function BridgeNodes() {
  const { apiClient } = useApi();
  const { isAuthenticated, authState } = useAuth();
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();
  const [nodes, setNodes] = useState<BridgeTypes.NodeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddNode, setShowAddNode] = useState(false);
  const [addStep, setAddStep] = useState(0);
  const [form, setForm] = useState({
    provider: 'S3',
    ip: '',
    status: 'active',
    region: '',
    version: '',
    timestamp: '',
    signature: '',
    id_org: '',
    privateKeyPem: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [detectedOrgId, setDetectedOrgId] = useState<string>('');
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const [generatingKey, setGeneratingKey] = useState<boolean>(false);
  const [showKeysModal, setShowKeysModal] = useState<boolean>(false);
  const [orgKeys, setOrgKeys] = useState<any[]>([]);
  const [loadingKeys, setLoadingKeys] = useState<boolean>(false);
  const [totalNodes, setTotalNodes] = useState<number>(0);

  // Helper: download a public key PEM
  const handleDownloadPublicKey = (keyId: string, publicKey: string) => {
    try {
      const blob = new Blob([publicKey || ''], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${keyId || 'public_key'}.pem`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download the public key');
    }
  };

  // Helper: copy public key to clipboard
  const handleCopyPublicKey = async (publicKey: string) => {
    try {
      await navigator.clipboard.writeText(publicKey || '');
    } catch (e) {
      // Fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = publicKey || '';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        alert('Failed to copy public key');
      }
    }
  };

  // Reusable loader for nodes
  const loadBridgeNodes = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const response = (await withTokenRefresh(() => apiClient.getNodeInfo(3))) as any;

      // Handle new response structure with success, data, total
      // Also handle legacy array response for backwards compatibility
      let nodesList: BridgeTypes.NodeInfo[] = [];

      if (response.success === true && Array.isArray(response.data)) {
        // New structure: { success: true, data: [], total: N }
        nodesList = response.data.map((n: BridgeTypes.NodeInfo) => ({
          url: String(n.url || ''),
          ip: String(n.ip || ''),
          region: String(n.region || ''),
          version: String(n.version || ''),
          status: String(n.status || ''),
          lastUpdated: String(n.lastUpdated || ''),
          nodeServices: Number(n.nodeServices || 3),
        }));
        setTotalNodes(response.total || nodesList.length);
      } else if (Array.isArray(response)) {
        // Legacy structure: direct array
        nodesList = response.map((n: any) => ({
          url: String(n.url || ''),
          ip: String(n.ip || ''),
          region: String(n.region || ''),
          version: String(n.version || ''),
          status: String(n.status || ''),
          lastUpdated: String(n.lastUpdated || ''),
          nodeServices: Number(n.nodeServices || 3),
        }));
        setTotalNodes(nodesList.length);
      } else {
        setTotalNodes(0);
      }

      setNodes(nodesList);
    } catch (error) {
      setNodes([]);
    } finally {
      setLoading(false);
    }
  };

  // --- Crypto helpers: PEM parsing and PKCS#1 -> PKCS#8 conversion ---
  const pemToArrayBuffer = (
    pem: string
  ): { der: ArrayBuffer; isPkcs1: boolean; isPkcs8: boolean } => {
    const isPkcs1 = /-----BEGIN RSA PRIVATE KEY-----/.test(pem);
    const isPkcs8 = /-----BEGIN PRIVATE KEY-----/.test(pem);
    const base64 = pem
      .replace(/-----BEGIN [^-]+-----/g, '')
      .replace(/-----END [^-]+-----/g, '')
      .replace(/\s+/g, '');
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return { der: bytes.buffer, isPkcs1, isPkcs8 };
  };

  // Build minimal PKCS#8 PrivateKeyInfo wrapping for an RSAPrivateKey DER
  const wrapPkcs1ToPkcs8 = (pkcs1Der: ArrayBuffer): ArrayBuffer => {
    // ASN.1 DER helpers
    const oidRsaEncryption = [1, 2, 840, 113549, 1, 1, 1];

    const encodeLength = (len: number): number[] => {
      if (len < 128) return [len];
      const bytes: number[] = [];
      let tmp = len;
      while (tmp > 0) {
        bytes.unshift(tmp & 0xff);
        tmp >>= 8;
      }
      return [0x80 | bytes.length, ...bytes];
    };

    const encodeInteger = (val: number): number[] => {
      // Only for small integers like version=0
      if (val === 0) return [0x02, 0x01, 0x00];
      const bytes: number[] = [];
      let tmp = val;
      while (tmp > 0) {
        bytes.unshift(tmp & 0xff);
        tmp >>= 8;
      }
      if (bytes[0] & 0x80) bytes.unshift(0x00);
      return [0x02, ...encodeLength(bytes.length), ...bytes];
    };

    const encodeNull = (): number[] => [0x05, 0x00];

    const encodeOid = (oid: number[]): number[] => {
      const [first, second, ...rest] = oid;
      const firstByte = 40 * first + second;
      const body: number[] = [firstByte];
      for (const v of rest) {
        const stack: number[] = [];
        let val = v;
        do {
          stack.unshift(val & 0x7f);
          val >>= 7;
        } while (val > 0);
        for (let i = 0; i < stack.length; i++) {
          const b = stack[i];
          body.push(i < stack.length - 1 ? b | 0x80 : b);
        }
      }
      return [0x06, ...encodeLength(body.length), ...body];
    };

    const encodeSequence = (content: number[]): number[] => [
      0x30,
      ...encodeLength(content.length),
      ...content,
    ];
    const encodeOctetString = (bytes: Uint8Array): number[] => [
      0x04,
      ...encodeLength(bytes.length),
      ...Array.from(bytes),
    ];

    const version = encodeInteger(0); // INTEGER 0
    const algorithm = encodeSequence([...encodeOid(oidRsaEncryption), ...encodeNull()]);
    const privateKey = encodeOctetString(new Uint8Array(pkcs1Der));
    const privateKeyInfo = encodeSequence([...version, ...algorithm, ...privateKey]);
    return new Uint8Array(privateKeyInfo).buffer;
  };

  useEffect(() => {
    loadBridgeNodes();
  }, [apiClient, isAuthenticated, navigate, withTokenRefresh]);

  // Prefill organization id by default on page load - use globalData instead of API call
  const { globalData } = useAuth();
  useEffect(() => {
    // Use globalData.userProfile instead of making API call
    if (globalData?.userProfile?.orgId) {
      const orgId = globalData.userProfile.orgId;
      setDetectedOrgId(orgId);
      setForm((f) => (f.id_org ? f : { ...f, id_org: orgId }));
    }
  }, [globalData?.userProfile?.orgId]);

  // Load current user profile when opening the Add Node modal - use globalData instead of API call
  useEffect(() => {
    if (showAddNode) {
      setLoadingProfile(true);
      // Use globalData.userProfile instead of making API call
      if (globalData?.userProfile?.orgId) {
        const orgId = globalData.userProfile.orgId;
        setDetectedOrgId(orgId);
        setForm((f) => (f.id_org ? f : { ...f, id_org: orgId }));
      } else {
        setDetectedOrgId('');
      }
      setLoadingProfile(false);
    }
  }, [showAddNode, globalData?.userProfile?.orgId]);

  // Generate organization private key (PEM) and prefill
  const handleGenerateOrgKey = async () => {
    const idOrg = form.id_org || detectedOrgId;
    if (!idOrg) {
      alert('Organization ID is required to generate a key.');
      return;
    }
    const token = authState?.token;
    if (!token) {
      alert('You must be authenticated to generate a key.');
      return;
    }
    try {
      setGeneratingKey(true);
      const response = (await withTokenRefresh(() => apiClient.generateOrgKey(idOrg)));

      // PEM content
      const pemText = await response as string;
      // Prefill textarea
      setForm((f) => ({ ...f, privateKeyPem: pemText }));
      // Download as file
      const blob = new Blob([pemText], { type: 'application/x-pem-file' });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = 'private_key.pem';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dlUrl);
    } catch (e: any) {
      alert(e?.message || 'Failed to generate organization key');
    } finally {
      setGeneratingKey(false);
    }
  };

  // Fonction pour formater les dates
  const formatDateTime = (iso: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const handleOpenKeysModal = async () => {
    const idOrg = form.id_org || detectedOrgId;
    if (!idOrg) {
      alert('Organization ID required to list keys.');
      return;
    }
    const token = authState?.token;
    if (!token) {
      alert('You must be authenticated to list organization keys.');
      return;
    }
    try {
      setLoadingKeys(true);
      setShowKeysModal(true);
      const response = (await withTokenRefresh(() => apiClient.getOrgKeys(idOrg))) as any;
      if (!response.success) {
        const msg = response.message || `Failed to fetch keys`;
        alert(msg);
        setOrgKeys([]);
      } else {
        const list = Array.isArray(response.data) ? response.data : [];
        setOrgKeys(list);
      }
    } catch (e: any) {
      alert(e?.message || 'Failed to retrieve organization keys');
      setOrgKeys([]);
    } finally {
      setLoadingKeys(false);
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Network
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Monitor and manage your bridge nodes infrastructure
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Public Bridge Nodes Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
                Public Bridge Nodes
              </Typography>
              {!loading && totalNodes > 0 && (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}
                >
                  {totalNodes} node{totalNodes !== 1 ? 's' : ''}
                </span>
              )}
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#667085', mt: 0.5 }}>
              Monitor latency and availability of bridge nodes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Keys */}
            <Tooltip title="View organization keys">
              <IconButton
                size="small"
                onClick={handleOpenKeysModal}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <VisibilityIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* Reload */}
            <Tooltip title="Reload">
              <IconButton
                size="small"
                onClick={() => loadBridgeNodes()}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* Add */}
            <Tooltip title="Add node">
              <IconButton
                size="small"
                onClick={() => {
                  setForm((f) => ({ ...f, timestamp: new Date().toISOString() }));
                  setAddStep(0);
                  setShowAddNode(true);
                }}
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <div className="border-b border-gray-200 mb-6"></div>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : nodes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
          <Typography variant="h6" className="text-gray-600 mb-2">
            No public bridge nodes available
          </Typography>
          <Typography variant="body2" className="text-gray-500">
            Public bridge nodes will appear here once they are registered
          </Typography>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-[#E9EAEB]">
          <table className="w-full table-auto">
            <thead>
              <tr
                className="border-b border-gray-200"
                style={{ backgroundColor: 'white', height: '44px' }}
              >
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Support
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {nodes.map((node, idx) => (
                <tr
                  key={`${node.url}-${idx}`}
                  className="hover:bg-gray-50 transition-all"
                  style={{ height: '72px' }}
                >
                  <td className="px-4 py-4 truncate">
                    <a
                      href={node.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:underline font-medium"
                      style={{ color: '#4F46E5' }}
                    >
                      {node.url || '-'}
                    </a>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {node.ip || '-'}
                    </Typography>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {node.region || '-'}
                    </Typography>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Chip
                      label={node.version || '-'}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500, borderColor: '#E5E7EB', color: '#374151' }}
                    />
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Chip
                      label={node.status || '-'}
                      size="small"
                      sx={{ fontWeight: 500, backgroundColor: '#F3F4F6', color: '#374151' }}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '12px', mb: 1 }}>Supported Services</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                              <Typography sx={{ fontSize: '12px' }}>Storage (S3 compatible)</Typography>
                            </Box>
                            {(node as any).aiSupport && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#8B5CF6' }} />
                                <Typography sx={{ fontSize: '12px' }}>AI Gateway (LLM Proxy)</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box sx={{ display: 'flex', gap: 0.5, cursor: 'pointer' }}>
                        {/* Multiport if both Storage AND AI are supported */}
                        {(node as any).aiSupport ? (
                          <Chip
                            label="Multiport"
                            size="small"
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: '11px',
                              height: '22px',
                              backgroundColor: '#DBEAFE', 
                              color: '#1E40AF',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        ) : (
                          <Chip
                            label="Storage"
                            size="small"
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: '11px',
                              height: '22px',
                              backgroundColor: '#D1FAE5', 
                              color: '#065F46',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-4 truncate">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {formatDateTime(node.lastUpdated)}
                    </Typography>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Organization Keys Modal - Sidebar style */}
      {showKeysModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white w-[560px] max-w-[90vw] rounded-xl border border-[#E9EAEB] p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[16px] font-semibold text-[#101828]">Organization keys</span>
              <button className="text-gray-500" onClick={() => setShowKeysModal(false)}>
                ✕
              </button>
            </div>
            {loadingKeys ? (
              <div className="text-sm text-gray-600">Loading keys…</div>
            ) : orgKeys.length === 0 ? (
              <div className="text-sm text-gray-600">No keys found for this organization.</div>
            ) : (
              <div className="max-h-[60vh] overflow-auto">
                <ul className="divide-y divide-gray-100">
                  {orgKeys.map((k: any) => {
                    const nodesCount = Array.isArray(k.nodeKeys) ? k.nodeKeys.length : 0;
                    // Build a compact preview of the public key (first/last lines)
                    const pk: string = k.publicKey || '';
                    const lines = pk.split(/\r?\n/).filter(Boolean);
                    const firstLine = lines[0] || '';
                    const lastLine = lines[lines.length - 1] || '';
                    let middle = '';
                    if (lines.length > 2) {
                      const content = lines.slice(1, -1).join('');
                      const prefix = content.slice(0, 32);
                      const suffix = content.slice(-32);
                      middle = `${prefix}…${suffix}`;
                    }
                    return (
                      <li key={k.id} className="py-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div
                                className="text-[14px] font-semibold text-[#101828] truncate"
                                title={k.id}
                              >
                                {k.id}
                              </div>
                            </div>
                            <div className="mt-1 text-[12px] text-gray-600">
                              <div className="text-[12px] font-semibold mb-1">
                                Public key (preview)
                              </div>
                              <div className="text-[11px] bg-[#FAFAFA] border border-[#E9EAEB] rounded p-2 overflow-hidden">
                                <div className="truncate" title={firstLine}>
                                  {firstLine}
                                </div>
                                {middle && (
                                  <div className="truncate" title={middle}>
                                    {middle}
                                  </div>
                                )}
                                <div className="truncate" title={lastLine}>
                                  {lastLine}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="shrink-0 text-right text-[12px] text-gray-600">
                            <div className="flex items-center gap-2 justify-end mb-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full border border-[#E9EAEB] bg-[#FAFAFA] text-[#414651]">
                                Associated nodes: {nodesCount}
                              </span>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                className="h-8 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[12px] font-semibold"
                                onClick={() => handleCopyPublicKey(pk)}
                              >
                                Copy
                              </button>
                              <button
                                className="h-8 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[12px] font-semibold"
                                onClick={() => handleDownloadPublicKey(k.id, pk)}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <button
                className="h-9 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[14px] font-semibold"
                onClick={() => setShowKeysModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Bridge Node Modal - styled like Sidebar's Create workspace */}
      {showAddNode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white w-[360px] rounded-xl border border-[#E9EAEB] p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[16px] font-semibold text-[#101828]">Add bridge node</span>
              <button className="text-gray-500" onClick={() => setShowAddNode(false)}>
                ✕
              </button>
            </div>
            {/* Step 0 - Node details */}
            {addStep === 0 && (
              <div className="space-y-3">
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">Provider</label>
                  <select
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                    value={form.provider}
                    onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  >
                    <option value="S3">S3</option>
                    <option value="GCS">GCS</option>
                    <option value="AZURE">AZURE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">IP address</label>
                  <input
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                    value={form.ip}
                    onChange={(e) => setForm({ ...form, ip: e.target.value })}
                    placeholder="1.2.3.4"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">Region</label>
                  <input
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                    placeholder="eu-west-1"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">Version</label>
                  <input
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    placeholder="1.0.0"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">Status</label>
                  <select
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 1 - Org key & signature */}
            {addStep === 1 && (
              <div className="space-y-3">
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">
                    Organization ID (optional)
                  </label>
                  <input
                    className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                    value={form.id_org}
                    onChange={(e) => setForm({ ...form, id_org: e.target.value })}
                    placeholder="org_..."
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-[12px] text-gray-500">Private Key PEM</label>
                    <div className="flex items-center gap-2">
                      <button
                        className="h-8 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[12px] font-semibold disabled:opacity-60"
                        onClick={handleGenerateOrgKey}
                        disabled={generatingKey || !(form.id_org || detectedOrgId)}
                        title={
                          !(form.id_org || detectedOrgId) ? 'Set an Organization ID first' : ''
                        }
                      >
                        {generatingKey ? 'Generating…' : 'Generate from org'}
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="w-full border border-gray-200 rounded-lg p-2 text-[13px] min-h-[140px]"
                    value={form.privateKeyPem}
                    onChange={(e) => setForm({ ...form, privateKeyPem: e.target.value })}
                    placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                  />
                </div>
                <div className="flex items-center justify-between text-[12px] text-gray-600">
                  <span>Timestamp: {form.timestamp || '-'}</span>
                  <button
                    className="h-8 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[12px] font-semibold"
                    onClick={() => setForm((f) => ({ ...f, timestamp: new Date().toISOString() }))}
                  >
                    Refresh timestamp
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 - Review */}
            {addStep === 2 && (
              <div className="space-y-2 text-sm text-gray-800">
                <div>
                  <strong>Provider:</strong> {form.provider}
                </div>
                <div>
                  <strong>IP:</strong> {form.ip}
                </div>
                <div>
                  <strong>Status:</strong> {form.status}
                </div>
                <div>
                  <strong>Region:</strong> {form.region}
                </div>
                <div>
                  <strong>Version:</strong> {form.version}
                </div>
                <div>
                  <strong>Organization ID:</strong> {form.id_org || '-'}
                </div>
                <div>
                  <strong>Timestamp:</strong> {form.timestamp || '-'}
                </div>
                <div className="pt-2 border-t border-gray-100 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      Detected organization id{loadingProfile ? ' (loading...)' : ''}:
                      <span className="ml-1 font-medium text-gray-900">{detectedOrgId || '-'}</span>
                    </span>
                    <button
                      className="h-8 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[12px] font-semibold disabled:opacity-60"
                      disabled={!detectedOrgId}
                      onClick={() => setForm((f) => ({ ...f, id_org: detectedOrgId }))}
                    >
                      Use
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              {addStep > 0 && (
                <button
                  className="h-9 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[14px] font-semibold"
                  onClick={() => setAddStep((s) => Math.max(0, s - 1))}
                >
                  Back
                </button>
              )}
              {addStep < 2 && (
                <button
                  className="h-9 px-3 rounded-lg bg-[#617BFF] text-white text-[14px] font-semibold disabled:opacity-60"
                  onClick={() => setAddStep((s) => s + 1)}
                  disabled={addStep === 0 && (!form.ip || !form.region || !form.version)}
                >
                  Next
                </button>
              )}
              {addStep === 0 && (
                <button
                  className="h-9 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[14px] font-semibold"
                  onClick={() => setShowAddNode(false)}
                >
                  Cancel
                </button>
              )}
              {addStep === 2 && (
                <button
                  className="h-9 px-3 rounded-lg bg-[#617BFF] text-white text-[14px] font-semibold disabled:opacity-60"
                  disabled={submitting || !form.privateKeyPem}
                  onClick={async () => {
                    /*
                    try {
                      setSubmitting(true);
                      // Ensure we always use a fresh timestamp right before signing
                      const nowIso = new Date().toISOString();
                      const payload = {
                        provider: form.provider,
                        ip: form.ip,
                        status: form.status,
                        region: form.region,
                        version: form.version,
                        timestamp: form.timestamp || nowIso,
                        id_org: form.id_org || undefined,
                      } as any;

                      // Sign payload using RSA-SHA256 with provided private key
                      const encoder = new TextEncoder();
                      const dataToSign = encoder.encode(JSON.stringify(payload));

                      // Parse PEM and support PKCS#1 and PKCS#8
                      const { der, isPkcs1, isPkcs8 } = pemToArrayBuffer(form.privateKeyPem || '');
                      const pkcs8Der = isPkcs1 ? wrapPkcs1ToPkcs8(der) : der;

                      let cryptoKey: CryptoKey;
                      try {
                        cryptoKey = await window.crypto.subtle.importKey(
                          'pkcs8',
                          pkcs8Der,
                          { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
                          false,
                          ['sign']
                        );
                      } catch (e: any) {
                        alert(
                          'Invalid or unsupported private key format. Please paste an unencrypted RSA private key (PKCS#8 or PKCS#1). If PKCS#1 (BEGIN RSA PRIVATE KEY), use the Generate button or convert to PKCS#8.'
                        );
                        return;
                      }

                      let signatureBuf: ArrayBuffer;
                      try {
                        signatureBuf = await window.crypto.subtle.sign(
                          'RSASSA-PKCS1-v1_5',
                          cryptoKey,
                          dataToSign
                        );
                      } catch (e: any) {
                        alert(
                          'Failed to sign payload with the provided private key. Ensure the key matches your organization and is an RSA key for PKCS#1 v1.5 with SHA-256.'
                        );
                        return;
                      }
                      const sigBytes = new Uint8Array(signatureBuf);
                      let binary = '';
                      for (let i = 0; i < sigBytes.length; i++) {
                        binary += String.fromCharCode(sigBytes[i]);
                      }
                      const signature = btoa(binary);

                      const body = { ...payload, signature };
                      const response = (await withTokenRefresh(() => apiClient.nodeRegister(body))) as any;

                      const result = await res.json();
                      if (result?.success) {
                        setShowAddNode(false);
                        await loadBridgeNodes();
                      } else {
                        alert(result?.message || 'Registration failed');
                      }
                    } catch (e: any) {
                      alert(e?.message || 'Registration error');
                    } finally {
                      setSubmitting(false);
                    }
                  }*/
                  }}
                >
                  {submitting ? 'Registering…' : 'Register node'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default BridgeNodes;
